import React, {
  useMemo,
  useState,
  useEffect,
  useCallback
} from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import {
  Box,
  Flex,
  Button,
  Spinner,
  Paragraph,
  usePaginationReducer,
  useOrderableTableReducer
} from '@ubisend/pulse-components';
import { useQuery } from '@ubisend/pulse-hooks';
import { PermissionFilter } from '@ubisend/pulse-auth';

import useSharePointState from './SharePoint/useSharePointState';
import ModalManager from './SharePoint/ModalManager';
import SiteTable from './SharePoint/SiteTable';

dayjs.extend(relativeTime);

const SharePoint = () => {
  const state = useSharePointState();

  const {
    crawlSites,
    activeModal,
    queryClient,
    selectedSite,
    tokenInvalid,
    globalLoading,
    setCrawlSites,
    setActiveModal,
    setTokenInvalid,
    disconnectMutation
  } = state;

  const [forceRefetchOnce, setForceRefetchOnce] = useState(false);

  const order = useOrderableTableReducer({ id: 'generative_ai' });
  const pagination = usePaginationReducer({ id: 'generative_ai' });

  const sharePointStatusQueryKey = useMemo(
    () => ['generative-ai/sharepoint/status', { ...pagination.params, ...order.params }],
    [pagination.params, order.params]
  );

  const isSitePanelOpen = Boolean(selectedSite);

  const sharePointStatus = useQuery({
    queryKey: sharePointStatusQueryKey,
    // Always allow a single fetch when token is valid (sorting/pagination shouldn't get stuck).
    enabled: !tokenInvalid,
    // Poll only when:
    // - token is valid
    // - server indicates work (handled via crawlSites)
    // - NO site panel is open (stop status crawling while viewing folders/resources)
    refetchInterval: (!tokenInvalid && crawlSites && !isSitePanelOpen) ? 5000 : false
  });

  const data = sharePointStatus.data;

  // Handle token invalidity
  useEffect(() => {
    const message =
      sharePointStatus.error?.response?.data?.message ||
      sharePointStatus.error?.message;
    if (message === 'Token is invalid, please reconnect.') {
      setTokenInvalid(true);
      setCrawlSites(false); // stop polling immediately
    }
  }, [sharePointStatus.error, setTokenInvalid, setCrawlSites]);

  // Close modal if token invalid (except credential-related modals)
  useEffect(() => {
    if (tokenInvalid && activeModal && !['updateCredential', 'disconnect'].includes(activeModal)) {
      setActiveModal(null);
    }
  }, [tokenInvalid, activeModal, setActiveModal]);

  // Control polling based on server status and panel-open state
  useEffect(() => {
    const hasPending = data?.has_pending === true;

    // We only want to poll when there's activity AND the site panel is not open.
    const shouldPoll = !isSitePanelOpen && hasPending;

    // Flip crawlSites only when it differs (avoid churn).
    if (shouldPoll && !crawlSites) {
      setCrawlSites(true);
    } else if (!shouldPoll && crawlSites) {
      setCrawlSites(false);
    }

    // One-off refetch trigger, if requested elsewhere.
    if (forceRefetchOnce) {
      queryClient.invalidateQueries({ queryKey: ['generative-ai/sharepoint/status'] });
      setForceRefetchOnce(false);
    }
  }, [crawlSites, queryClient, setCrawlSites, isSitePanelOpen, forceRefetchOnce, data?.has_pending]);

  const handleUpdateCredential = useCallback(() => setActiveModal('updateCredential'), [setActiveModal]);
  const handleAddSite = useCallback(() => setActiveModal('addSite'), [setActiveModal]);
  const handleDisconnect = useCallback(() => setActiveModal('disconnect'), [setActiveModal]);

  const disableActions = disconnectMutation.isPending || data?.has_pending;

  const renderActionButtons = useCallback(() => (
    <Flex center right flex wrap="true" gap="sm" mt>
      <Button
        ml
        icon="plus"
        variant="secondary"
        disabled={disableActions}
        onClick={handleAddSite}
      >
        Add site
      </Button>
      <Button
        ml
        icon="key"
        variant="secondary"
        disabled={disableActions}
        onClick={handleUpdateCredential}
      >
        Update Credential
      </Button>
      <Button
        ml
        icon="trash"
        variant="secondary"
        colour="danger"
        disabled={disableActions}
        isLoading={disconnectMutation.isPending}
        onClick={handleDisconnect}
      >
        Disconnect
      </Button>
    </Flex>
  ), [handleAddSite, disableActions, handleDisconnect, handleUpdateCredential, disconnectMutation.isPending]);

  // Conditional rendering
  if (tokenInvalid) {
    return (
      <>
        <Flex col>
          <Box border="dashed">
            <Flex col middle center>
              <Paragraph mb secondary>
                Token invalid, please update your credential to continue.
              </Paragraph>
              <PermissionFilter can="edit generative ai resource">
                <Flex center right flex mt>
                  <Button
                    ml
                    icon="key"
                    variant="secondary"
                    onClick={handleUpdateCredential}
                  >
                    Update Credential
                  </Button>
                  <Button
                    ml
                    icon="trash"
                    variant="secondary"
                    colour="danger"
                    onClick={handleDisconnect}
                  >
                    Disconnect
                  </Button>
                </Flex>
              </PermissionFilter>
            </Flex>
          </Box>
        </Flex>
        <ModalManager state={state} />
      </>
    );
  }

  if (sharePointStatus.isLoading || !data) {
    return (
      <Flex col center middle tall>
        <Spinner />
        <Paragraph mt>Loading SharePoint...</Paragraph>
      </Flex>
    );
  }

  if (data.status === 'no_credentials') {
    return (
      <>
        <Flex col>
          <Box border="dashed">
            <Flex col middle center>
              <Paragraph mb secondary>
                Connect your SharePoint account to sync support data.
              </Paragraph>
              <PermissionFilter can="edit generative ai resource">
                <Button
                  icon="plus"
                  variant="primary"
                  onClick={() => setActiveModal('registration')}
                  disabled={globalLoading || sharePointStatus.isLoading}
                >
                  Connect SharePoint
                </Button>
              </PermissionFilter>
            </Flex>
          </Box>
        </Flex>
        <ModalManager state={state} />
      </>
    );
  }

  if (data.status === 'no_sites') {
    return (
      <>
        <Flex col>
          <Box border="dashed">
            <Flex col middle center>
              <Paragraph mb secondary>
                Connect your SharePoint account to sync support data. Connect a site.
              </Paragraph>
              <PermissionFilter can="edit generative ai resource">
                {renderActionButtons()}
              </PermissionFilter>
            </Flex>
          </Box>
        </Flex>
        <ModalManager state={state} />
      </>
    );
  }

  return (
    <>
      <SiteTable data={data} state={state} orderProps={order.props} />

      <PermissionFilter can="edit generative ai resource">
        {data.data?.length > 0 && renderActionButtons()}
      </PermissionFilter>

      <ModalManager state={state} />
    </>
  );
};

export default SharePoint;